"use client";

/**
 * Mis direcciones 5.3 — lista + alta de dirección con zona.
 */

import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";

import { BottomNav, HeaderTitulo } from "@/components/app/chrome";
import { useApp } from "@/components/app/proveedor";
import type { ZonaApp } from "@/components/app/tipos";

interface Direccion {
  id: number;
  etiqueta: string;
  direccion: string;
  zonaSlug: string;
  principal: boolean;
}

export function DireccionesCliente({ zonas }: { zonas: ZonaApp[] }) {
  const router = useRouter();
  const { usuario, zonaSlug } = useApp();
  const [direcciones, setDirecciones] = useState<Direccion[] | null>(null);
  const [agregando, setAgregando] = useState(false);
  const [etiqueta, setEtiqueta] = useState("Casa");
  const [texto, setTexto] = useState("");
  const [zonaNueva, setZonaNueva] = useState("");
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(() => {
    fetch("/api/cuenta/direcciones")
      .then((r) => r.json())
      .then((data) => setDirecciones(data.ok ? data.direcciones : []))
      .catch(() => setDirecciones([]));
  }, []);

  useEffect(() => {
    if (usuario === null) router.replace("/acceso?next=/cuenta/direcciones");
    if (usuario) cargar();
  }, [usuario, router, cargar]);

  useEffect(() => {
    if (!zonaNueva && zonaSlug) setZonaNueva(zonaSlug);
  }, [zonaSlug, zonaNueva]);

  async function guardar() {
    setError(null);
    const res = await fetch("/api/cuenta/direcciones", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        etiqueta,
        direccion: texto,
        zonaSlug: zonaNueva,
        principal: (direcciones?.length ?? 0) === 0,
      }),
    });
    const data = await res.json();
    if (!res.ok || !data.ok) {
      setError(data.error ?? "No pudimos guardar.");
      return;
    }
    setAgregando(false);
    setTexto("");
    cargar();
  }

  async function eliminar(id: number) {
    await fetch(`/api/cuenta/direcciones?id=${id}`, { method: "DELETE" });
    cargar();
  }

  function nombreZona(slug: string): string {
    return zonas.find((z) => z.slug === slug)?.nombre ?? slug;
  }
  function diaZona(slug: string): string {
    return zonas.find((z) => z.slug === slug)?.diaLabel ?? "";
  }

  return (
    <>
      <HeaderTitulo titulo="Mis direcciones" href="/cuenta" />
      <main className="anim-bz-in min-h-(--alto-app) px-6 pt-[100px] pb-[62px]">
        <div className="flex flex-col gap-3 pt-[22px] pb-[30px]">
          {direcciones === null ? (
            <p className="pt-8 text-center text-sm text-tinta/50">Cargando…</p>
          ) : (
            direcciones.map((d) => (
              <div
                key={d.id}
                className={`rounded-3xl bg-white p-[18px] ${d.principal ? "border-[1.5px] border-verde" : "border border-black/5"}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-app text-base font-semibold">{d.etiqueta}</span>
                  {d.principal && (
                    <span className="rounded-[14px] bg-lima px-2.5 py-1 text-[11px] font-bold text-bosque">
                      Principal
                    </span>
                  )}
                </div>
                <p className="mt-2 text-[13px] leading-[19px] text-tinta/65">
                  {d.direccion}
                </p>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-verde">
                    {nombreZona(d.zonaSlug)} · {diaZona(d.zonaSlug)}
                  </span>
                  <button
                    type="button"
                    onClick={() => void eliminar(d.id)}
                    className="text-xs text-tinta/45 underline"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            ))
          )}

          {agregando ? (
            <div className="rounded-3xl border border-black/5 bg-white p-[18px]">
              <input
                value={etiqueta}
                onChange={(e) => setEtiqueta(e.target.value)}
                placeholder="Etiqueta (Casa, Oficina…)"
                className="h-[52px] w-full rounded-[18px] border border-black/10 px-4 text-base outline-0"
              />
              <input
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder="Dirección exacta"
                className="mt-2.5 h-[52px] w-full rounded-[18px] border border-black/10 px-4 text-base outline-0"
              />
              <div className="mt-2.5 flex flex-wrap gap-2">
                {zonas.map((z) => (
                  <button
                    key={z.slug}
                    type="button"
                    onClick={() => setZonaNueva(z.slug)}
                    className={`h-9 rounded-[18px] px-3.5 text-[13px] ${zonaNueva === z.slug ? "bg-bosque text-white" : "bg-humo text-tinta"}`}
                  >
                    {z.nombre}
                  </button>
                ))}
              </div>
              {error && <p className="mt-2 text-sm text-[#B4463A]">{error}</p>}
              <div className="mt-3.5 flex gap-2.5">
                <button
                  type="button"
                  onClick={() => void guardar()}
                  className="flex h-11 flex-1 items-center justify-center rounded-[22px] bg-verde font-app text-sm font-semibold text-white"
                >
                  Guardar
                </button>
                <button
                  type="button"
                  onClick={() => setAgregando(false)}
                  className="flex h-11 items-center rounded-[22px] border border-black/10 px-4 font-app text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAgregando(true)}
              className="flex h-14 items-center justify-center rounded-[28px] border border-dashed border-verde/50 font-app text-[15px] font-medium text-verde"
            >
              + Agregar dirección
            </button>
          )}

          <div className="rounded-[20px] bg-humo p-4 text-[13px] leading-[19px] text-tinta/65">
            Solo entregamos en las {zonas.length} zonas de cobertura. El día de
            entrega depende de la zona, no lo eliges tú.
          </div>
        </div>
      </main>
      <BottomNav />
    </>
  );
}
